<svelte:options
  customElement={{
    tag: "oc-icon-button-v3",
    extend: window.__components.extend({}),
    props: {
      backgroundColor: { type: "String", attribute: "background-color" },
      base64Href: { type: "String", attribute: "base64-href" },
      disabled: { type: "Boolean", reflect: true },
      elevationLevel: { type: "String", attribute: "elevation-level" },
      icon: { type: "String" },
      iconColor: { type: "String", attribute: "icon-color" },
      label: { type: "String" },
      loading: { type: "Boolean" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      rel: { type: "String" },
      size: { type: "String" },
      success: { type: "Boolean", reflect: true },
      target: { type: "String" },
      variant: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import { fade } from "svelte/transition";
  import type { OcSpinnerV1Props } from "../../spinner";
  import type { OcIconV1Props } from "../../icon/v1/IconV1.types.g";
  import { InteractiveElement } from "../../../common/components/InteractiveElement";

  import type { Props } from "./IconButtonV3.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    variant = "secondary-over-color",
    elevationLevel = undefined,
    backgroundColor = undefined,
    disabled = false,
    icon,
    iconColor = undefined,
    size = "50",
    label = undefined,
    loading = false,
    success = false,
    ocAriaLabel,
    base64Href = undefined,
    rel = undefined,
    role = null,
    target = undefined,
  }: Props & {
    role?: string | null;
  } = $props();

  const Host = $host<HTMLElement>();

  const spinnerVariant: Record<Exclude<typeof variant, undefined>, OcSpinnerV1Props["variant"]> = {
    "secondary-over-color": "default",
    "secondary-on-color": "default",
    primary: "inverted",
    secondary: "default",
    tertiary: "default",
    transparent: "default",
    "transparent-inverted": "default",
    "custom-color-strong": "inverted",
    "custom-color-soft": "default",
  };

  const spinnerSize: Record<Exclude<typeof size, undefined>, OcSpinnerV1Props["size"]> = {
    "100": "100",
    "75": "100",
    "50": "100",
    "25": "50",
  };

  const iconSize: Record<Exclude<typeof size, undefined>, OcIconV1Props["size"]> = {
    "100": "100",
    "75": "100",
    "50": "100",
    "25": "50",
  };

  const handleClick = (event: MouseEvent | KeyboardEvent): void => {
    if (loading) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  Host.addEventListener("click", handleClick);
</script>

<!-- eslint-disable svelte/no-at-html-tags -->
<!-- eslint-disable no-nested-ternary -->
{#snippet interactiveElement()}
  <InteractiveElement
    {Host}
    asButton={role === null || role !== "none"}
    bind:base64Href
    class={`icon-button icon-button--size-${size} ${elevationLevel ? `icon-button--elevation-${elevationLevel}` : ""} icon-button--variant-${variant} ${variant === "secondary-on-color" ? "icon-button--variant-secondary-over-color" : ""} ${disabled ? "icon-button--disabled" : ""} ${loading ? " icon-button--is-loading" : ""} ${success ? "icon-button--success" : ""}`}
    style={`${backgroundColor ? `--background-color: ${backgroundColor};` : ""}`}
    {ocAriaLabel}
    {disabled}
    aria-disabled={disabled || loading}
    {rel}
    {target}
  >
    {#snippet children(onslotchange)}
      {#if loading}
        <oc-spinner-v1
          in:fade={{ duration: 250 }}
          class="icon-button__icon"
          variant={spinnerVariant[variant]}
          size={spinnerSize[size ?? "50"]}
        ></oc-spinner-v1>
      {:else}
        <oc-icon-v1
          aria-hidden="true"
          class="icon-button__icon"
          class:icon-button__icon--disabled={disabled}
          size={iconSize[size ?? "50"]}
          type={icon}
          color={disabled ? undefined : iconColor}
        ></oc-icon-v1>
      {/if}
      <slot {onslotchange} />
    {/snippet}
  </InteractiveElement>
{/snippet}

{#if label}
  <oc-tooltip-v1 position="top">
    {@render interactiveElement()}
    <div aria-hidden="true" slot="tooltip-content">{label}</div>
  </oc-tooltip-v1>
{:else}
  {@render interactiveElement()}
{/if}

<DefaultStyles display="inline-block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "../../floating-focus/v1/FloatingFocusV1.shared" as floating-focus;
  @use "../../../common/scss/SvelteComponent.mixins" as *;
  :global {
    :host {
      outline: none;
      vertical-align: middle;

      /*                                                                        */
      --background-color: initial;
      --icon-color: initial;
    }

    :host([disabled]) {
      pointer-events: none;
    }

    ::slotted(*) {
      all: unset;
    }

    /*                                                */
    .icon-button:before,
    ::slotted(*) {
      content: "";
      position: absolute;
      width: 100%;
      height: 100%;
      min-width: var(--hitbox-size);
      min-height: var(--hitbox-size);
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      @include floating-focus.full-bleed();
    }

    /*                 */
    .icon-button {
      --hitbox-size: #{tokens.$oc-component-icon-button-50-hitbox-size};
      display: block;
      position: relative;
      border: none;
      border-radius: tokens.$oc-component-icon-button-50-border-radius; /*  */
      height: tokens.$oc-component-icon-button-50-background-size;
      width: tokens.$oc-component-icon-button-50-background-size;
      cursor: pointer;
      outline: none;
      outline-offset: 0;

      @include interactiveOverlayBase();
      @include interactiveOverlayColors();

      &--size-100 {
        border-radius: tokens.$oc-component-icon-button-100-border-radius;
        width: tokens.$oc-component-icon-button-100-background-size;
        height: tokens.$oc-component-icon-button-100-background-size;
      }

      &--size-75 {
        border-radius: tokens.$oc-component-icon-button-75-border-radius;
        width: tokens.$oc-component-icon-button-75-background-size;
        height: tokens.$oc-component-icon-button-75-background-size;
      }

      &--size-25 {
        border-radius: tokens.$oc-component-icon-button-25-border-radius;
        width: tokens.$oc-component-icon-button-25-background-size;
        height: tokens.$oc-component-icon-button-25-background-size;
        --hitbox-size: #{tokens.$oc-component-icon-button-25-hitbox-size};
      }

      &--elevation-canvas {
        background-color: var(--background-color, tokens.$oc-semantic-color-canvas-background);
        filter: tokens.$oc-semantic-shadow-canvas;
      }

      &--elevation-above {
        background-color: var(--background-color, tokens.$oc-semantic-color-above-background);
        filter: tokens.$oc-semantic-shadow-above;
      }

      &--elevation-sticky {
        background-color: var(--background-color, tokens.$oc-semantic-color-sticky-background);
        filter: tokens.$oc-semantic-shadow-sticky;
      }

      &--variant-secondary-over-color {
        background-color: tokens.$oc-component-icon-button-secondary-over-color-background-color;

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: #{tokens.$oc-component-icon-button-secondary-over-color-icon-color};
        }
      }

      &--variant-primary {
        background-color: tokens.$oc-component-icon-button-primary-background-color;

        @include interactiveOverlayColors($inverted: true);

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: #{tokens.$oc-component-icon-button-primary-icon-color};
        }
      }

      &--variant-secondary {
        background-color: tokens.$oc-component-icon-button-secondary-background-color;

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: #{tokens.$oc-component-icon-button-secondary-icon-color};
        }
      }

      &--variant-tertiary {
        background-color: tokens.$oc-component-icon-button-transparent-background-color;

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: #{tokens.$oc-component-icon-button-tertiary-icon-color};
        }
      }

      &--variant-transparent {
        background-color: tokens.$oc-component-icon-button-transparent-background-color;

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: #{tokens.$oc-component-icon-button-transparent-icon-color};
        }
      }

      &--variant-transparent-inverted {
        background-color: tokens.$oc-component-icon-button-inverted-background-color;

        @include interactiveOverlayColors($inverted: true);

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: #{tokens.$oc-component-icon-button-inverted-icon-color};
        }
      }

      &--variant-custom-color-strong {
        background-color: var(
          --background-color,
          tokens.$oc-component-icon-button-primary-background-color
        );

        @include interactiveOverlayColors($inverted: true);

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: var(--icon-color, #{tokens.$oc-component-icon-button-primary-icon-color});
        }
      }

      &--variant-custom-color-soft {
        background-color: var(
          --background-color,
          tokens.$oc-component-icon-button-secondary-background-color
        );

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: var(--icon-color, #{tokens.$oc-component-icon-button-secondary-icon-color});
        }
      }

      &__icon {
        --color: var(
          --icon-color,
          #{tokens.$oc-component-icon-button-secondary-over-color-icon-color}
        );
        left: 50%;
        top: 50%;
        position: absolute;
        transform: translate(-50%, -50%);

        &--disabled {
          --color: #{tokens.$oc-component-icon-button-disabled-icon-color};
        }
      }

      &:disabled,
      &.icon-button--disabled {
        background-color: tokens.$oc-component-icon-button-disabled-background-color;
      }

      &.icon-button--success,
      &.icon-button--success.icon-button--disabled,
      &.icon-button--success:disabled {
        background-color: tokens.$oc-component-icon-button-success-background-color;

        @include interactiveOverlayColors();

        /*                                                           */
        .icon-button__icon {
          --color: #{tokens.$oc-component-icon-button-success-icon-color};
        }

        .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
          --color: var(--icon-color, #{tokens.$oc-component-icon-button-success-icon-color});
        }
      }

      &:not(.icon-button--is-loading) {
        @media (hover: hover) {
          &:hover:not(:disabled):not(.icon-button--disabled) {
            @include interactiveOverlayHover();
          }
        }

        &:active:not(:disabled):not(.icon-button--disabled) {
          @include interactiveOverlayActive();
        }
      }

      &--is-loading {
        cursor: progress;
      }
    }

    oc-tooltip-v1 {
      width: 100%;
      display: block;
      line-height: 0;
    }
  }
</style>
